import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

export const ContactSection = ({ profile }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSent(true);
      setFormData({ name: "", email: "", message: "" });
      toast.success(response.data.message || "Message sent successfully!");
    } catch (e) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="contact-section"
      aria-label="Contact Tommy Green"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <span className="mono-label text-primary mb-3 block">Get in Touch</span>
            <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Contact Me
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto">
              Have a question or want to work together? Drop me a line.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <motion.div variants={reveal}>
              <Card className="p-6 md:p-8 bg-card border-border">
                {sent ? (
                  <div className="text-center py-8" data-testid="contact-form-success-message">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--font-display)' }}>Message Sent!</h3>
                    <p className="text-sm text-muted-foreground mb-4">Thanks for reaching out. I'll get back to you soon.</p>
                    <Button
                      variant="secondary"
                      onClick={() => setSent(false)}
                      className="bg-secondary text-secondary-foreground border border-border"
                    >
                      Send Another
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="mono-label text-muted-foreground mb-2 block">Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="bg-muted border-border focus:ring-2 focus:ring-primary/30"
                        data-testid="contact-form-name-input"
                      />
                    </div>
                    <div>
                      <label className="mono-label text-muted-foreground mb-2 block">Email</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-muted border-border focus:ring-2 focus:ring-primary/30"
                        data-testid="contact-form-email-input"
                      />
                    </div>
                    <div>
                      <label className="mono-label text-muted-foreground mb-2 block">Message</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="What's on your mind?"
                        rows={5}
                        className="bg-muted border-border focus:ring-2 focus:ring-primary/30 resize-none"
                        data-testid="contact-form-message-textarea"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      style={{ boxShadow: 'var(--shadow-glow-pink)' }}
                      data-testid="contact-form-submit-button"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={reveal} className="flex flex-col gap-4">
              <Card className="p-6 bg-card border-border glow-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="mono-label text-muted-foreground mb-1">Email</div>
                    <a
                      href={`mailto:${profile.email}`}
                      className="text-foreground hover:text-primary transition-colors duration-200"
                      data-testid="contact-email-link"
                    >
                      {profile.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-card border-border glow-card glow-card-gold">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <div className="mono-label text-muted-foreground mb-1">Phone</div>
                    <a
                      href={`tel:${profile.phone}`}
                      className="text-foreground hover:text-accent transition-colors duration-200"
                      data-testid="contact-phone-link"
                    >
                      {profile.phone}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-card border-border glow-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="mono-label text-muted-foreground mb-1">Location</div>
                    <span className="text-foreground">{profile.location}</span>
                  </div>
                </div>
              </Card>

              {/* Quick note */}
              <Card className="p-6 bg-secondary/50 border-border mt-2">
                <p className="text-sm text-muted-foreground leading-[1.55] mb-4">
                  Whether you're looking for a business partner, need security services, 
                  want to discuss gaming, or just want to grab a boba tea — don't hesitate to reach out.
                </p>
                {/* Social Media Links */}
                {profile.social_links && (
                  <div className="flex items-center gap-3 pt-3 border-t border-border">
                    <span className="mono-label text-muted-foreground mr-1">Follow</span>
                    {profile.social_links.instagram && (
                      <a href={profile.social_links.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="contact-social-instagram" className="text-muted-foreground hover:text-pink-400 transition-colors duration-200">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                      </a>
                    )}
                    {profile.social_links.facebook && (
                      <a href={profile.social_links.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="contact-social-facebook" className="text-muted-foreground hover:text-blue-400 transition-colors duration-200">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                      </a>
                    )}
                    {profile.social_links.tiktok && (
                      <a href={profile.social_links.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="contact-social-tiktok" className="text-muted-foreground hover:text-cyan-300 transition-colors duration-200">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
                      </a>
                    )}
                    {profile.social_links.youtube && (
                      <a href={profile.social_links.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="contact-social-youtube" className="text-muted-foreground hover:text-red-400 transition-colors duration-200">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/></svg>
                      </a>
                    )}
                    {profile.social_links.twitch && (
                      <a href={profile.social_links.twitch} target="_blank" rel="noreferrer" aria-label="Twitch" data-testid="contact-social-twitch" className="text-muted-foreground hover:text-purple-400 transition-colors duration-200">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2H3v16h5v4l4-4h5l4-4V2zM11 11V7M16 11V7"/></svg>
                      </a>
                    )}
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
